import {
  AutocompleteNoOptions,
  Button,
  Callout,
  FormField,
  Modal,
  Skeleton,
} from '@dev-spendesk/grapes';
import { toNumber, type MonetaryValue } from 'ezmoney';
import { useState } from 'react';

import { type LocaleFormat, useTranslation } from 'common/hooks/useTranslation';
import {
  type Trip,
  useCurrentTripsQuery,
} from 'modules/requests/hooks/useCurrentTripsQuery';
import { getTripDates, getTripDestination } from 'modules/requests/utils/trips';
import { unwrapQuery } from 'src/core/api/unwrapQuery';
import { CalloutError } from 'src/core/common/components/LazyLoad';
import { useUser } from 'src/core/modules/app/hooks/useUser';
import { TripSelect } from 'src/core/modules/requests/components/TripSelect';

type Props = {
  booking: {
    price: MonetaryValue;
    dates: {
      from: Date;
      to: Date;
    };
    destination?: string | null;
  };
  isOpen: boolean;
  onClose: () => void;
  onBook: (tripId: string) => void;
};

type TripOption = {
  key: string;
  label: string;
  destination: string | null;
  destinationLabel: string;
  dateLabel: string;
  dates: {
    from: Date;
    to: Date;
  };
  budget?: {
    state: TripBudgetState;
  };
};

export type TripBudgetState = NonNullable<NonNullable<Trip['budget']>['state']>;

export const AddStayToTripModal = ({
  booking,
  isOpen,
  onClose,
  onBook,
}: Props) => {
  const { t, localeFormat } = useTranslation('global');
  const [selectedTripId, setSelectedTripId] = useState<string>();
  const [error, setError] = useState<string | undefined>();
  const user = useUser();
  const tripsQuery = useCurrentTripsQuery({ isEnabled: isOpen });
  const trips = unwrapQuery(tripsQuery)?.data.trips ?? [];

  const tripOptions = trips.map((trip) =>
    buildTripOption(
      trip,
      localeFormat,
      t('travel.propertyDetails.addStayModal.tripLabel'),
    ),
  );

  const handleClose = () => {
    setSelectedTripId(undefined);
    setError(undefined);
    onClose();
  };

  const selectedTrip = tripOptions.find((trip) => trip.key === selectedTripId);
  const handleAddBooking = () => {
    if (!selectedTrip) {
      setError(t('travel.propertyDetails.addStayModal.noTripSelected'));
      return;
    }
    if (!isBookingWithinBudget(selectedTrip.budget?.state, booking.price)) {
      setError(
        t('travel.propertyDetails.addStayModal.incompatibleBudgetReason'),
      );
      return;
    }
    if (!user.mobile_no) {
      setError(
        t('travel.propertyDetails.addStayModal.incompatiblePhoneNumberReason'),
      );
      return;
    }
    onBook(selectedTrip.key);
    handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('travel.propertyDetails.addStayModal.title')}
      subtitle={t('travel.propertyDetails.addStayModal.subtitle')}
      iconName="plane"
      iconVariant="purple"
      actions={[
        <Button
          key="cancel"
          variant="secondaryNeutral"
          onClick={handleClose}
          text={t('misc.cancel')}
        />,
        <Button
          key="add"
          variant="primaryBrand"
          onClick={handleAddBooking}
          text={t('travel.propertyDetails.addStayModal.cta')}
        />,
      ]}
    >
      <div className="flex flex-col gap-16">
        <FormField
          label={t('travel.propertyDetails.addStayModal.tripLabel')}
          className="w-full text-left"
        >
          {tripsQuery.isLoading && (
            <Skeleton width="100%" height="40px" className="rounded-8" />
          )}
          {tripsQuery.isError && (
            <CalloutError
              title={t('lazyLoading.error.component.title')}
              actionText={t('lazyLoading.error.retry')}
              retry={() => {
                tripsQuery.refetch();
              }}
            />
          )}
          {tripsQuery.isSuccess && (
            <TripSelect
              options={tripOptions}
              value={selectedTrip}
              placeholder={t(
                'travel.propertyDetails.addStayModal.tripPlaceholder',
              )}
              onSelect={(option) => setSelectedTripId(option.key)}
              renderNoOptions={() => (
                <AutocompleteNoOptions
                  variant="info"
                  className="body-m text-center"
                >
                  <p className="text-content-primary body-m mb-4 font-semibold">
                    {t('requests.panel.travelBudget.tripSelect.emptyTitle')}
                  </p>
                  <p className="text-content-secondary-bg-secondary body-s">
                    {t('requests.panel.travelBudget.tripSelect.emptyHint')}
                  </p>
                </AutocompleteNoOptions>
              )}
            />
          )}
        </FormField>
        {error && (
          <Callout
            variant="alert"
            title={t('travel.propertyDetails.addStayModal.incompatibleTitle')}
          >
            {error}
          </Callout>
        )}
      </div>
    </Modal>
  );
};

const buildTripOption = (
  trip: Trip,
  localeFormat: LocaleFormat,
  fallbackTripName: string,
): TripOption => {
  const budgetState = trip.budget?.state;

  const destination = getTripDestination(trip.route) ?? fallbackTripName;
  const startDate = new Date(trip.startDate);
  const endDate = new Date(trip.endDate);
  const dateLabel = getTripDates(startDate, endDate, localeFormat).trim();

  return {
    key: trip.id,
    label: `${destination} | ${dateLabel}`,
    destination,
    destinationLabel: destination,
    dateLabel,
    dates: { from: startDate, to: endDate },
    budget: budgetState
      ? {
          state: budgetState,
        }
      : undefined,
  };
};

const isBookingWithinBudget = (
  budgetState: TripBudgetState | undefined,
  bookingPrice: MonetaryValue,
) => {
  if (!budgetState?.available) {
    return true;
  }

  if (budgetState.available.currency !== bookingPrice.currency) {
    return false;
  }

  return toNumber(budgetState.available) >= toNumber(bookingPrice);
};
